import { graphql } from 'gatsby';
import queryString from 'query-string';
import React, { FC } from 'react';

import { useGetChallengeDetailPageLink } from '@/bits/links/useLink';
import {
  Card,
  CardBody,
  CardOptions,
  CardOptionsButton,
  ControlledModal,
  InlineIconButton,
} from '@/components';
import { useDataGrid } from '@/components/DataGrid';
import {
  NextIcon,
  PersonAddIcon,
  PersonRemoveIcon,
  PlusIcon,
  PreviousIcon,
  RefreshIcon,
  TrashIcon,
} from '@/components/icons';
import { useTranslate } from '@/contexts';
import {
  ChallengeCreateForm,
  ChallengePlayerAssignForm,
  ChallengePlayerRemoveForm,
  ChallengeRemoveForm,
} from '@/forms';
import { ChallengeExternalSystem, ChallengeStatus } from '@/globalTypes';
import { returnMultipleValues } from '@/utils';
import useChallenges from './useChallenges';

export const Fragment = graphql`
  fragment SanityChallengesHistoryBlock on SanityChallengesHistoryBlock {
    title {
      ...SanityLocaleString
    }
    name {
      ...SanityLocaleString
    }
    challengeId {
      ...SanityLocaleString
    }
    challengeUuid {
      ...SanityLocaleString
    }
    numberOfPlayers {
      ...SanityLocaleString
    }
    brands {
      ...SanityLocaleString
    }
    countries {
      ...SanityLocaleString
    }
    startDate {
      ...SanityLocaleString
    }
    endDate {
      ...SanityLocaleString
    }
    status {
      ...SanityLocaleString
    }
    actions {
      ...SanityLocaleString
    }
  }
`;

const shouldEnableActions = (status: ChallengeStatus) => {
  return (
    status === ChallengeStatus.Activated || status === ChallengeStatus.Scheduled
  );
};

const ChallengeHistoryBlock: FC<{
  block: Queries.SanityChallengesHistoryBlockFragment;
}> = ({ block }) => {
  const { t } = useTranslate();

  const getChallengeDetailPageLink = useGetChallengeDetailPageLink();

  const {
    challenges,
    fetching,
    nextPage,
    previousPage,
    refresh,
    orderBy,
    desc,
    setOrderBy,
    defaultFilter,
    updateFilter,
  } = useChallenges();

  const linkToMoreDetails = (challengeUUID: string, tabIndex: number) => {
    const basePath = getChallengeDetailPageLink(challengeUUID);

    if (!basePath) {
      return '';
    }

    return `${basePath}?${queryString.stringify({ tabIndex })}`;
  };

  const { DataGrid, GlobalCardOptions } = useDataGrid({
    name: 'challenges',
    data: challenges,
    fetching,
    orderBy,
    setOrderBy,
    desc,
    defaultFilter,
    updateFilter,
    schema: (s) => [
      s.stringValue({
        field: 'name',
        filterField: 'name',
        title: t(block.name),
        getValue: ({ row }) => row.name,
        linkTo: ({ row }) => linkToMoreDetails(row.challengeUUID, 1),
      }),
      s.stringValue({
        field: 'challengeId',
        title: t(block.challengeId),
        hidden: true,
      }),
      s.stringValue({
        field: 'challengeUUID',
        title: t(block.challengeUuid),
        filterField: 'challengeUUID',
        enableCopy: true,
      }),
      s.numberValue({
        field: 'numberOfPlayers',
        title: t(block.numberOfPlayers),
        linkTo: ({ row }) => linkToMoreDetails(row.challengeUUID, 1),
      }),
      s.stringValue({
        field: 'brands',
        title: t(block.brands),
        getValue: ({ row }) => returnMultipleValues(row.brands),
        linkTo: ({ row }) => linkToMoreDetails(row.challengeUUID, 0),
      }),
      s.stringValue({
        field: 'countries',
        title: t(block.countries),
        getValue: ({ row }) => returnMultipleValues(row.countries),
        linkTo: ({ row }) => linkToMoreDetails(row.challengeUUID, 0),
      }),
      s.stringValue({
        field: 'challengeActions',
        title: 'Challenge Action',
        hidden: true,
      }),
      s.dateTimeValue({
        field: 'startDate',
        title: t(block.startDate),
      }),
      s.dateTimeValue({
        field: 'endDate',
        title: t(block.endDate),
      }),
      s.enumValue({
        field: 'status',
        title: t(block.status),
        filterField: 'status',
        e: ChallengeStatus,
        isMulti: true,
        getValue: ({ row }) => row.status,
      }),
      s.enumValue({
        field: 'challengeSystem',
        title: 'Challenge System',
        filterField: 'challengeSystem',
        e: ChallengeExternalSystem,
        getValue: ({ row }) => row.challengeSystem,
      }),
      s.actionValue({
        field: 'actions',
        title: 'Actions',
        getValue: ({ row }) => (
          <div className="flex items-center">
            <ControlledModal
              content={
                <ChallengePlayerAssignForm
                  challengeId={row.challengeUUID}
                  challengeName={row.name}
                />
              }
            >
              <InlineIconButton
                disabled={!shouldEnableActions(row.status)}
                tippyContent="Assign Player(s)"
              >
                <PersonAddIcon />
              </InlineIconButton>
            </ControlledModal>
            <ControlledModal
              content={
                <ChallengePlayerRemoveForm
                  challengeId={row.challengeUUID}
                  challengeName={row.name}
                />
              }
            >
              <InlineIconButton
                disabled={
                  !shouldEnableActions(row.status) || row.numberOfPlayers === 0
                }
                tippyContent="Remove Player(s)"
              >
                <PersonRemoveIcon />
              </InlineIconButton>
            </ControlledModal>
            <ControlledModal
              content={<ChallengeRemoveForm challengeId={row.challengeUUID} />}
            >
              <InlineIconButton
                disabled={!shouldEnableActions(row.status)}
                tippyContent="Remove Challenge"
              >
                <TrashIcon />
              </InlineIconButton>
            </ControlledModal>
          </div>
        ),
      }),
    ],
  });

  return (
    <Card
      size="lg"
      title={t(block.title)}
      showOptionsAtBottom
      options={
        <CardOptions>
          <CardOptionsButton
            disabled={!previousPage}
            onClick={() => previousPage && previousPage()}
          >
            <PreviousIcon />
          </CardOptionsButton>
          <CardOptionsButton
            disabled={!nextPage}
            onClick={() => nextPage && nextPage()}
          >
            <NextIcon />
          </CardOptionsButton>
          <GlobalCardOptions />
          <CardOptionsButton
            tippyContent="Refresh"
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
          <ControlledModal
            content={
              <ChallengeCreateForm
                callback={() => refresh({ requestPolicy: 'network-only' })}
              />
            }
          >
            <CardOptionsButton tippyContent="Create Challenge">
              <PlusIcon />
            </CardOptionsButton>
          </ControlledModal>
        </CardOptions>
      }
    >
      <CardBody>
        <DataGrid />
      </CardBody>
    </Card>
  );
};

export default ChallengeHistoryBlock;
